import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode
} from "react";

/**
 * Three responsive buckets. ONE mobile cutoff at **768px**, matched in JS and
 * CSS (v4 pass-2 §2.1):
 *  - mobile  `< 768`
 *  - tablet  `768 – 1024`
 *  - desktop `> 1024`
 *
 * `<DeviceProvider>` listens on `resize` (covers orientation change) and updates
 * the active bucket; consumers read via `useDevice()`. When the bucket CHANGES,
 * subscribers registered through `useDeviceBucketTransition()` fire — the
 * planner uses this to close open surfaces, re-measure the sticky offset, and
 * drop transient drag/marquee state, since a grid↔scroller swap unmounts nodes.
 */
export type Device = "mobile" | "tablet" | "desktop";

// One mobile cutoff (§2.1). Mobile is < 768; tablet is 768–1024 inclusive.
const MOBILE_MAX = 767;
const TABLET_MAX = 1024;

const DeviceContext = createContext<Device>("desktop");

function classify(width: number): Device {
  if (width <= MOBILE_MAX) return "mobile";
  if (width <= TABLET_MAX) return "tablet";
  return "desktop";
}

/**
 * Fired AFTER the device bucket changes (resize / orientation). Payload is the
 * new and previous buckets so a listener can act on a specific crossing (e.g.
 * only the mobile↔tablet 767/768 seam, which swaps grid ↔ scroller).
 */
export type DeviceBucketTransition = { from: Device; to: Device };
type TransitionListener = (t: DeviceBucketTransition) => void;

const TransitionContext = createContext<{
  subscribe: (fn: TransitionListener) => () => void;
} | null>(null);

export function DeviceProvider({ children }: { children: ReactNode }) {
  const [device, setDevice] = useState<Device>(() =>
    typeof window === "undefined" ? "desktop" : classify(window.innerWidth)
  );

  // Transition subscribers. Kept in a ref so registering a listener never
  // re-runs the resize effect (which would drop and re-add the DOM handler).
  const listenersRef = useRef<Set<TransitionListener>>(new Set());
  const deviceRef = useRef<Device>(device);
  deviceRef.current = device;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => {
      const next = classify(window.innerWidth);
      const prev = deviceRef.current;
      if (next === prev) return;
      deviceRef.current = next;
      setDevice(next);
      for (const fn of listenersRef.current) fn({ from: prev, to: next });
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const transitionValue = useRef({
    subscribe: (fn: TransitionListener) => {
      listenersRef.current.add(fn);
      return () => {
        listenersRef.current.delete(fn);
      };
    }
  }).current;

  return (
    <DeviceContext.Provider value={device}>
      <TransitionContext.Provider value={transitionValue}>
        {children}
      </TransitionContext.Provider>
    </DeviceContext.Provider>
  );
}

export function useDevice(): Device {
  return useContext(DeviceContext);
}

/**
 * Register a callback that runs whenever the device bucket changes. The callback
 * is stored in a ref internally, so it always sees the latest closure without
 * needing a stable identity. Returns nothing — cleanup is automatic on unmount.
 *
 * Used centrally by the planner (§2.1): on a bucket transition it closes open
 * toolbar surfaces/sheets, re-measures `--fgp-sticky-top`, re-validates the
 * scroll anchor, and drops drag/marquee state — implemented once here, not
 * per-component.
 */
export function useDeviceBucketTransition(fn: TransitionListener): void {
  const ctx = useContext(TransitionContext);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    if (!ctx) return;
    return ctx.subscribe((t) => fnRef.current(t));
  }, [ctx]);
}
